import type { PropertyDetailsFragment } from '__generated__/athena/graphql';

export interface GalleryPhoto {
  url: string;
  label?: string | null;
  isFloorPlan: boolean;
}

// Builds the ordered set of gallery photos for a property by appending any
// available floor plan photos after the standard listing photos. Consumers
// (GalleryPhotos, PhotoCarousel, Lightbox) share this helper so the gallery
// index -> URL mapping stays consistent across surfaces; a click on the Nth
// tile in the gallery opens the same Nth photo in the lightbox.
export const buildGalleryPhotos = (
  property: Pick<PropertyDetailsFragment, 'photos' | 'floorPlanPhotos'> | null | undefined,
): GalleryPhoto[] => {
  const listingPhotos: GalleryPhoto[] =
    property?.photos?.map((url) => ({ url, isFloorPlan: false })) ?? [];
  const floorPlanPhotos: GalleryPhoto[] =
    property?.floorPlanPhotos
      ?.filter((fp) => !!fp?.url)
      .map((fp) => ({ url: fp.url, label: fp.label ?? null, isFloorPlan: true })) ?? [];
  return [...listingPhotos, ...floorPlanPhotos];
};

// Photos for carousels backed by bricks-next `PhotoCarousel`. Its internal
// `Image` uses `resizeMode: cover` with no per-photo override, so a
// naturally-portrait floor plan gets cover-cropped to fill the square/16:9
// slide regardless of which URL variant we pass. Until bricks-next exposes
// per-photo resize, keep floor plans out of those carousels and surface
// them in the rendering paths we control (desktop GalleryPhotos, mobile
// "see all photos" modal, Lightbox), all of which use resizeMode: contain.
export const buildCarouselPhotos = (
  property: Pick<PropertyDetailsFragment, 'photos' | 'floorPlanPhotos'> | null | undefined,
): GalleryPhoto[] => buildGalleryPhotos(property).filter((p) => !p.isFloorPlan);

// Floor plan source URL: deliberately omits the 3:2 crop preset used for
// listing photos because cabinet returns a cropped image at those presets
// and floor plans are often portrait/square, so cropping to 3:2 clips the
// edges. Callers should render the returned URL with object-fit: contain
// inside a fixed-aspect container so the full plan shows with letterboxing.
export const buildFloorPlanSrc = (url: string): string => {
  const params = new URLSearchParams({
    service: 'cosmos',
    auto: 'format,compress',
  });
  return `${url}?${params.toString()}`;
};

// Lightbox variant: bricks-next Lightbox hard-appends `?preset=3:2-md...`
// onto whatever URL it receives. For listing photos that's what we want;
// for floor plans we want to defeat the preset so the natural aspect is
// preserved (lightbox already applies resizeMode: contain). Returning a
// URL that already contains a `?` causes the lightbox's extra `?preset=...`
// suffix to be parsed as part of the prior query value, so the preset is
// never recognized and cabinet serves the uncropped image.
export const buildLightboxPhotoUrl = (photo: GalleryPhoto): string =>
  photo.isFloorPlan ? buildFloorPlanSrc(photo.url) : photo.url;

export const buildLightboxPhotoUrls = (
  property: Pick<PropertyDetailsFragment, 'photos' | 'floorPlanPhotos'> | null | undefined,
): string[] => buildGalleryPhotos(property).map(buildLightboxPhotoUrl);
